import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppStateInterface } from 'src/app/appState.interface';
import { getCustomersSelector } from 'src/app/core/store/selectors';
import { ApiService } from 'src/app/core/services/api.service';
import { DataService } from 'src/app/core/services/data.service';
import { ONE_CUSTOMER_ENDPOINT } from 'src/app/core/config/config.constants';

// Tetto di sicurezza anti-ciclo/dati anomali per la risalita via backend (vedi climbExternalAncestors$).
const MAX_EXTERNAL_ANCESTOR_HOPS = 25;

@Injectable({
  providedIn: 'root'
})
export class CustomerHierarchyService {
  private parentMap = new Map<string, string>();

  // Prefisso di antenati "esterni" (sopra il customer dell'utente loggato, mai presenti in
  // parentMap perché ofCustomer/{uid} restituisce solo discendenti). Cache chiavata per uid
  // dell'utente loggato -- NON un singolo flag "calcolato una volta per sempre" -- così un
  // cambio utente senza reload di pagina (es. dopo AuthService.sessionExpired(), che non fa
  // window.location.reload()) non riusa per errore il prefisso del vecchio utente.
  private externalAncestorPrefixCache = new Map<string, Observable<string | null>>();

  constructor(
    private store: Store<AppStateInterface>,
    private apiService: ApiService,
    private dataService: DataService
  ) {
    // Aggiornamento in background, disaccoppiato da qualunque richiesta HTTP:
    // la lista customer cambia tipicamente una sola volta per sessione (al login).
    this.store.select(getCustomersSelector).subscribe(customers => {
      this.parentMap = new Map((customers || []).map(c => [c.uid, c.uidParent]));
    });
  }

  /**
   * Risale la gerarchia completa da `uid` (self + antenati, inclusi quelli sopra il customer
   * dell'utente loggato) e restituisce il percorso slash-separated da passare come
   * x-customer-id, es. "UID_Nonno/UID_Padre/UID_Figlio". Ritorna null se uid è vuoto.
   */
  buildAncestorPath$(uid: string | undefined): Observable<string | null> {
    if (!uid) return of(null);

    // Letto ad ogni chiamata (non in costruttore/una tantum): se l'utente non è ancora
    // caricato in questo preciso momento, niente viene messo in cache e si riprova al
    // prossimo giro, invece di restare bloccati su un prefisso "null" per tutta la sessione.
    const myUid = this.dataService.getCurrentUserInfo()?.customer?.uid;
    const localPath = this.buildLocalAncestorPath(uid);

    if (!myUid) return of(localPath);

    return this.getExternalAncestorPrefix$(myUid).pipe(
      map(prefix => [prefix, localPath].filter(Boolean).join('/') || null)
    );
  }

  /**
   * Risale la gerarchia nota localmente (parentMap, popolata solo con i discendenti del
   * customer dell'utente loggato) da `uid` fino al punto più alto conosciuto.
   * Ritorna null se uid è vuoto/non trovato.
   */
  private buildLocalAncestorPath(uid: string | undefined): string | null {
    if (!uid || !this.parentMap.has(uid)) return null;

    const path: string[] = [];
    const visited = new Set<string>(); // guardia anti-ciclo su dati malformati
    let current: string | undefined = uid;
    while (current && !visited.has(current)) {
      visited.add(current);
      path.push(current);
      current = this.parentMap.get(current) || undefined;
    }
    return path.reverse().join('/');
  }

  private getExternalAncestorPrefix$(myUid: string): Observable<string | null> {
    if (!this.externalAncestorPrefixCache.has(myUid)) {
      this.externalAncestorPrefixCache.set(
        myUid,
        this.climbExternalAncestors$(myUid).pipe(shareReplay(1))
      );
    }
    return this.externalAncestorPrefixCache.get(myUid)!;
  }

  /**
   * Risale via backend (ONE_CUSTOMER_ENDPOINT, stesso endpoint già usato da
   * CustomerNameService per risolvere il nome di un antenato non presente localmente) gli
   * antenati del customer dell'utente loggato, non essendo mai presenti in parentMap.
   */
  private climbExternalAncestors$(startUid: string): Observable<string | null> {
    const segments: string[] = [];
    const visited = new Set<string>([startUid]);

    const step = (uid: string): Observable<string | null> =>
      this.apiService.get(`${ONE_CUSTOMER_ENDPOINT}/${uid}`).pipe(
        map((customer: any) => customer?.uidParent || null),
        catchError(() => of(null)),
        switchMap(parentUid => {
          if (!parentUid || visited.has(parentUid) || segments.length >= MAX_EXTERNAL_ANCESTOR_HOPS) {
            return of(segments.length ? segments.reverse().join('/') : null);
          }
          visited.add(parentUid);
          segments.push(parentUid);
          return step(parentUid);
        })
      );

    return step(startUid);
  }
}
